import { httpService } from '../utils/http';
import { 
  ApiResponse, 
  Page, 
  Order, 
  OrderQueryRequest,
  YeepayRefundCreateRequest,
  YeepayRefundRecord,
  OrderStatisticsData,
  OrderStatisticsQueryRequest,
  OrderStatisticsRealtimeRequest,
  OrderStatisticsRecalculateRequest
} from '../types';

/**
 * 订单服务类
 * 提供订单相关的API接口调用
 */
export class OrderService {
  private readonly baseUrl = '/order';

  /**
   * 分页查询订单列表 (v2.0)
   * @param params 查询参数
   * @returns Promise<ApiResponse<Page<Order>>>
   */
  async getOrderList(params: OrderQueryRequest): Promise<ApiResponse<Page<Order>>> {
    return httpService.post(`${this.baseUrl}/list`, params);
  }

  /**
   * 获取订单详情 (v2.0)
   * @param orderId 订单ID
   * @returns Promise<ApiResponse<Order>>
   */
  async getOrderDetail(orderId: number): Promise<ApiResponse<Order>> {
    return httpService.get(`${this.baseUrl}/${orderId}`);
  }

  /**
   * 取消订单 (v2.0)
   * @param orderId 订单ID
   * @returns Promise<ApiResponse<boolean>>
   */
  async cancelOrder(orderId: number): Promise<ApiResponse<boolean>> {
    return httpService.post(`${this.baseUrl}/${orderId}/cancel`);
  }

  /**
   * 退款订单 (v2.0)
   * @param orderId 订单ID
   * @param refundAmount 退款金额
   * @param remark 退款备注
   * @returns Promise<ApiResponse<boolean>>
   */
  async refundOrder(orderId: number, refundAmount: number, remark?: string): Promise<ApiResponse<boolean>> {
    return httpService.post(`${this.baseUrl}/${orderId}/refund`, {
      refundAmount,
      remark,
    });
  }

  /**
   * 同步易宝订单状态（后台管理）
   */
  async syncYeepayOrderStatus(orderNo: string): Promise<ApiResponse<Order>> {
    return httpService.post('/payment/yeepay/order/sync', { orderNo });
  }

  /**
   * 发起易宝退款（后台管理）
   */
  async createYeepayRefund(orderNo: string, params: YeepayRefundCreateRequest): Promise<ApiResponse<YeepayRefundRecord>> {
    return httpService.post('/payment/yeepay/refund/create', {
      orderNo,
      ...params,
    });
  }

  /**
   * 查询退款记录（后台管理）
   */
  async queryRefundRecords(params: { orderId?: number; orderNo?: string }): Promise<ApiResponse<YeepayRefundRecord[]>> {
    return httpService.post('/payment/yeepay/refund/list', params);
  }

  /**
   * 同步退款状态（后台管理）
   */
  async syncRefundStatus(refundRequestId: string): Promise<ApiResponse<YeepayRefundRecord>> {
    return httpService.post('/payment/yeepay/refund/sync', { refundRequestId });
  }

  /**
   * 导出订单数据 (v2.0)
   * @param params 查询参数
   * @returns Promise<Blob>
   */
  async exportOrders(params: Omit<OrderQueryRequest, 'pageNo' | 'pageSize'>): Promise<Blob> {
    const response = await fetch(`${(httpService as any).baseURL}${this.baseUrl}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  // ==================== 订单统计相关接口 ====================

  /**
   * 查询订单统计数据（历史数据，来自统计表）
   * @param params 查询参数
   * @returns Promise<ApiResponse<Page<OrderStatisticsData>>>
   */
  async queryStatistics(params: OrderStatisticsQueryRequest): Promise<ApiResponse<Page<OrderStatisticsData>>> {
    return httpService.post(`${this.baseUrl}/statistics/query`, params);
  }

  /**
   * 查询实时统计数据（当天0点到当前时刻）
   * @param params 查询参数
   * @returns Promise<ApiResponse<OrderStatisticsData[]>>
   */
  async getRealtimeStatistics(params: OrderStatisticsRealtimeRequest = {}): Promise<ApiResponse<OrderStatisticsData[]>> {
    return httpService.post(`${this.baseUrl}/statistics/realtime`, params);
  }

  /**
   * 手动重算统计数据（管理员功能）
   * @param params 重算参数
   * @returns Promise<ApiResponse<void>>
   */
  async recalculateStatistics(params: OrderStatisticsRecalculateRequest): Promise<ApiResponse<void>> {
    return httpService.post(`${this.baseUrl}/statistics/recalculate`, params);
  }
}

// 创建默认实例
export const orderService = new OrderService();

// 导出默认实例
export default orderService;
